import { MyBatches } from '@/hooks/useMyBatches';
import { BatchDetailModel, BatchType } from '@/types/batch';
import { CampaignType } from '@/types/marketing';
import { findBatchType } from '@/utils/common';
import clsx, { type ClassValue } from 'clsx';
import { type Ref, type RefCallback } from 'react';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Merge multiple refs into a single ref callback
 * Similar to react-merge-refs functionality
 *
 * @param refs - Array of refs to merge (can be ref objects, ref callbacks, or null/undefined)
 * @returns A single ref callback that will update all provided refs
 *
 * @example
 * const Component = forwardRef<HTMLDivElement, Props>((props, ref) => {
 *   const internalRef = useRef<HTMLDivElement>(null)
 *
 *   return <div ref={mergeRefs(ref, internalRef)} {...props} />
 * })
 */
export function mergeRefs<T = any>(...refs: Array<Ref<T> | undefined | null>): RefCallback<T> {
  return (value: T | null) => {
    refs.forEach(ref => {
      if (typeof ref === 'function') {
        // RefCallback
        ref(value);
      } else if (ref != null) {
        // RefObject
        (ref as React.MutableRefObject<T | null>).current = value;
      }
    });
  };
}

export function formatTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  } else {
    return `${secs}s`;
  }
}

export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

  if (diffInHours < 1) return 'just now';
  if (diffInHours < 24) return `${diffInHours}h ago`;

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays}d ago`;

  return date.toLocaleDateString();
}

export function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export const getAssetCdnUrl = (path: string) => {
  if (path) {
    return `https://static.pw.live/production-curiousjr-fundoo/static/images/${path}`;
  }
  return '';
};

export const getEventCameFrom = (campaignType?: CampaignType): string => {
  if (typeof window === 'undefined') return '';
  if (window.location.pathname === '/') {
    return 'landing_page';
  }
  if (campaignType === 'MLP') {
    return 'market';
  }
  return 'blp';
};

export const getStartingScheduleOfBatch = (batchDetail: BatchDetailModel) => {
  return batchDetail?.subjects
    ?.map(subject => {
      return subject?.schedules;
    })
    ?.flat()
    ?.sort((a, b) => new Date(a?.startTime).getTime() - new Date(b?.startTime).getTime())?.[0];
};

export const getCommonMetaFields = (
  user?: { countryGroup?: string; countryCode?: string },
  batchData?: { fee?: { currency?: string; amount?: number }; countryGroup?: string }
) => {
  const currentUrl = typeof window !== 'undefined' ? window.location.href.split('?')[0] : '';
  // const user = getUserData();
  return {
    country_code: user?.countryCode || '',
    user_country: user?.countryGroup || '',
    batch_country: batchData?.countryGroup || '',
    batch_currency: batchData?.fee?.currency || '',
    batch_price: batchData?.fee?.amount ? String(batchData.fee.amount) : '',
    cohort_country: user?.countryGroup || '',
    source_link:
      currentUrl ||
      process.env.NEXT_PUBLIC_CJR_LINK ||
      process.env.NEXT_PUBLIC_SITE_URL ||
      'https://curiousjr.com',
    user_state: '',
    user_city: '',
  };
};

export const getTrackRoute = (type: 'AnimatedTrack' | 'PracticeTrack' | 'BytesTrack'): string => {
  switch (type) {
    case 'PracticeTrack':
      return '/v2/tracks/practice';

    case 'AnimatedTrack':
      return '/v2/tracks/animated';

    case 'BytesTrack':
      return '/v2/tracks/brainBytes';

    default:
      // This should never happen with proper TypeScript, but just in case
      console.warn(`Unknown offering type: ${type}. Redirecting to practice.`);
      return '/v2/tracks/practice';
  }
};

export const getHeaderBackground = (
  cohort: string | undefined,
  paidUserSelectedBatch?: MyBatches | null
) => {
  if (paidUserSelectedBatch) {
    return findBatchType(paidUserSelectedBatch) === BatchType.BOOSTER
      ? 'linear-gradient(180deg, #E2F5FF, #F3FBFF)'
      : 'linear-gradient(180deg, #FFF0D1, #FFFDF8)';
  }

  // For home page, use cohort-based gradient
  switch (cohort) {
    case 'ENGLISH_BY_CAMBRIDGE':
      return 'linear-gradient(180deg, #D9FFE6 2.85%, #FFF 7.94%)';
    case 'MENTAL_MATHS':
      return 'linear-gradient(180deg, #D1E0F3 2.85%, #FFF 7.94%)';
    case 'SCHOOL_CURRICULUM':
      return 'linear-gradient(180deg, #FCE5D2 2.85%, #FFF 7.94%)';
    default:
      return 'linear-gradient(180deg, #FCE5D2 2.85%, #FFF 7.94%)'; // Default to SCHOOL_CURRICULUM
  }
};
export const ensureMaxImagePreview = (value: string) =>
  value.includes('max-image-preview:large') ? value : `${value}, max-image-preview:large`;

export type SearchParams = { [key: string]: string | string[] | undefined };

export function buildRedirectUrl(baseUrl: string, searchParams: SearchParams): string {
  if (!searchParams || Object.keys(searchParams).length === 0) {
    return baseUrl;
  }

  const query = new URLSearchParams(
    Object.entries(searchParams).reduce<Record<string, string>>((acc, [key, value]) => {
      if (Array.isArray(value)) {
        acc[key] = value.join(','); // or expand into multiple entries if preferred
      } else if (typeof value !== 'undefined') {
        acc[key] = String(value);
      }
      return acc;
    }, {})
  ).toString();

  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}${query}`;
}

export const META_DATA_API = '/public/pages/meta';
